import * as React from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';

/**
 * Pure read-side helper: extracts only the given field IDs' non-empty array
 * values from a raw search object. Exported so callers merging multiple
 * filter-state sources (see `useMediaUnitsPage`) can compose reads without
 * instantiating a full `useSharedFilters` hook per source.
 */
export function readSharedFilterValues(
  search: Record<string, unknown>,
  fieldIds: readonly string[],
): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const id of fieldIds) {
    const vals = search[id];
    if (Array.isArray(vals) && vals.length > 0) result[id] = vals as string[];
  }
  return result;
}

/**
 * Pure write-side helper: applies `fieldIds`' values from `value` onto `current`,
 * stripping empty arrays. Exported so a caller combining multiple filter-state
 * sources (e.g. shared filters + catalog-only selects + numeric ranges) can fold
 * every source into ONE search object before a single `navigate()` call — calling
 * each source's own hook setter independently would race (each starts from the
 * same stale `search` snapshot, so the last setter's `navigate()` call clobbers
 * every earlier one). See `useMediaUnitsPage`'s `handleCombinedFiltersChange`.
 */
export function buildNextSharedFilterSearch(
  current: Record<string, unknown>,
  fieldIds: readonly string[],
  value: Record<string, string[]>,
): Record<string, unknown> {
  const next = { ...current };
  for (const id of fieldIds) {
    const vals = value[id];
    if (vals && vals.length > 0) {
      next[id] = vals;
    } else {
      delete next[id];
    }
  }
  return next;
}

/**
 * Reads filter state from TanStack Router URL search params and provides a setter
 * that writes changes back via navigate() with replace: true.
 *
 * @param fieldIds - The filter field IDs this hook should read and write.
 * @returns [filterValue, setFilterValue]
 *   - filterValue: Record<string, string[]> with only non-empty arrays for known field IDs
 *   - setFilterValue: writes the new filter values to the URL, preserving non-filter params,
 *     stripping empty arrays, using replace: true
 */
export function useSharedFilters(
  fieldIds: readonly string[],
): [Record<string, string[]>, (value: Record<string, string[]>) => void] {
  // structuralSharing: false required when the selected type is not JSON-safe
  const search = useSearch({ strict: false, structuralSharing: false }) as Record<string, unknown>;
  const navigate = useNavigate();

  const filterValue = React.useMemo<Record<string, string[]>>(
    () => readSharedFilterValues(search, fieldIds),
    [search, fieldIds],
  );

  const setFilterValue = React.useCallback(
    (value: Record<string, string[]>) => {
      // Functional search updater: reads LIVE state at commit time instead of a
      // captured snapshot, so a setter that fires close to another filter-state
      // setter (shared filters, catalog-only selects, ranges, POI params, ...)
      // never clobbers what the other just wrote (CU-86cataqt9 fix).
      // Cast is necessary: navigate() without `from` has no concrete search type.
      type AnyNavigate = (opts: {
        search: (prev: Record<string, unknown>) => Record<string, unknown>;
        replace: boolean;
      }) => void;
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => buildNextSharedFilterSearch(prev, fieldIds, value),
        replace: true,
      });
    },
    [navigate, fieldIds],
  );

  return [filterValue, setFilterValue];
}
